import { motion } from "framer-motion";
import { ArrowDownRight, MessageCircle, ShoppingBag } from "lucide-react";
import { SITE } from "@/data/site";
import { openWhatsApp } from "@/lib/whatsapp";

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="hero-bg relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      {/* faint crown silhouette */}
      <div className="absolute -top-20 -right-20 w-[520px] h-[520px] opacity-[0.05] pointer-events-none">
        <img src="/logo-gray.jpg" alt="" className="w-full h-full object-contain" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="eyebrow inline-flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-[hsl(var(--nl-champagne))]" />
            {SITE.tagline}
          </motion.span>

          <motion.h1
            data-testid="hero-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-display text-[2.6rem] leading-[1.05] sm:text-5xl lg:text-[4.6rem] mt-5 text-[hsl(var(--nl-anthracite))]"
          >
            Minimal Dokunuşlarla
            <br />
            <em className="not-italic font-serif italic gold-text">Zamansız Şıklık</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-7 max-w-xl text-base lg:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed"
          >
            {SITE.hero.subtitle} Sevgiyle seçilen kolye, küpe, bileklik ve yüzük koleksiyonlarımızla
            tarzınıza zarif bir dokunuş katın.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-collection"
              onClick={() => scrollTo("koleksiyonlar")}
              className="btn-gold"
            >
              <span>Koleksiyonu İncele</span>
              <ArrowDownRight size={16} />
            </button>
            <button
              data-testid="hero-cta-whatsapp"
              onClick={() => openWhatsApp()}
              className="btn-outline"
            >
              <MessageCircle size={16} />
              <span>WhatsApp ile İletişim</span>
            </button>
          </motion.div>

          <motion.a
            data-testid="hero-shopier-link"
            href={SITE.social.shopier}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="btn-ghost-gold mt-5 inline-flex"
          >
            <ShoppingBag size={12} /> Shopier Mağazamızı Ziyaret Et
          </motion.a>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="mt-14 flex items-center gap-8 text-xs uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]"
          >
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--nl-champagne))]" />
              <span>El Emeği Özen</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--nl-champagne))]" />
              <span>Kararmaz Çelik</span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--nl-champagne))]" />
              <span>Hızlı Kargo</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="hero-frame p-3 bg-white/60">
            <div className="relative overflow-hidden">
              <motion.img
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: "easeOut" }}
                src="/shopier/p-7.jpeg"
                alt="NovaLand öne çıkan koleksiyon"
                className="w-full h-[460px] lg:h-[560px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--nl-anthracite)/0.15)] via-transparent to-transparent" />
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: -12, y: 12 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="absolute -bottom-8 -left-6 lg:-left-12 bg-white px-6 py-5 shadow-[0_30px_60px_-30px_rgba(42,42,42,0.4)] border border-[hsl(var(--nl-line))] max-w-[260px]"
          >
            <p className="eyebrow">Vitrin</p>
            <p className="font-serif text-2xl mt-1 text-[hsl(var(--nl-anthracite))]">200+ Parça</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
              Kolye, küpe, bileklik, yüzük ve halhal — Shopier&apos;de güvenle.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
